import React, { useEffect, useRef, useMemo, useState } from 'react';
import { View, Text, Animated, TouchableOpacity } from 'react-native';
import Markdown from 'react-native-markdown-display';
import DbIcon from '../../DbIcon';
import DbChatLoading from '../DbChatLoading';
import { styles } from './styles';
import type { DbChatBubbleProps, SenderType, MessageDirection } from './types';

/** 根据发送者类型和方向获取气泡配色 */
const getBubbleColors = (senderType: SenderType, direction: MessageDirection) => {
    if (direction === 'right') {
        return { bg: '#007AFF', text: '#FFFFFF' };
    }

    switch (senderType) {
        case 'ai':
            return { bg: '#E8F4F8', text: '#1A1A1A' };
        case 'customer-service':
            return { bg: '#F2F2F7', text: '#1A1A1A' };
        case 'system':
            return { bg: 'transparent', text: '#999999' };
        default:
            return { bg: '#F2F2F7', text: '#1A1A1A' };
    }
};

/** 流式输出光标动画 */
const StreamingCursor: React.FC = () => {
    const opacity = useRef(new Animated.Value(1)).current;

    useEffect(() => {
        const anim = Animated.loop(
            Animated.sequence([
                Animated.timing(opacity, { toValue: 0, duration: 500, useNativeDriver: true }),
                Animated.timing(opacity, { toValue: 1, duration: 500, useNativeDriver: true }),
            ]),
        );
        anim.start();
        return () => anim.stop();
    }, [opacity]);

    return <Animated.Text style={[styles.cursor, { opacity }]}>▌</Animated.Text>;
};

/** 安全的 Markdown 渲染组件 */
const SafeMarkdown: React.FC<{
    content: string;
    textColor: string;
    direction: MessageDirection;
    isStreaming?: boolean;
}> = ({ content, textColor, direction, isStreaming }) => {
    const [hasError] = useState(false);

    const markdownStyles = useMemo(() => ({
        body: {
            color: textColor,
        },
        paragraph: {
            marginTop: 0,
            marginBottom: 0,
        },
        text: {
            color: textColor,
            fontSize: 15,
            lineHeight: 22,
        },
        strong: {
            fontWeight: 'bold' as const,
        },
        em: {
            fontStyle: 'italic' as const,
        },
        code_inline: {
            backgroundColor: direction === 'right' ? 'rgba(255,255,255,0.2)' : '#E8E8E8',
            borderRadius: 3,
            paddingHorizontal: 4,
            paddingVertical: 1,
            fontFamily: 'monospace',
            fontSize: 13,
        },
        code_block: {
            backgroundColor: direction === 'right' ? 'rgba(0,0,0,0.3)' : '#2D2D2D',
            borderRadius: 8,
            padding: 12,
            marginVertical: 6,
            color: '#E0E0E0', // 深色背景下用浅色文字
        },
        link: {
            color: direction === 'right' ? '#E0E0FF' : '#007AFF',
        },
    }), [textColor, direction]);

    // 如果 Markdown 渲染出错，回退到纯文本
    if (hasError) {
        return (
            <Text style={{ color: textColor, fontSize: 15, lineHeight: 22 }}>
                {content}
                {isStreaming && <StreamingCursor />}
            </Text>
        );
    }

    return (
        <View onStartShouldSetResponder={() => true}>
            <Markdown style={markdownStyles}>
                {content}
            </Markdown>
            {isStreaming && <StreamingCursor />}
        </View>
    );
};

/** 判断内容是否包含 Markdown 标记 */
const containsMarkdown = (content: string): boolean => {
    if (!content || typeof content !== 'string') { return false; }
    // 检测常见 Markdown 标记：标题 #、加粗 **、斜体 *、代码 `、链接 []、列表 - 或数字.
    // 也检测换行符 \n（Markdown 列表、段落依赖换行）
    return /[*#`\[\]\n-]|\d+\.\s/.test(content);
};

const DbChatBubble: React.FC<DbChatBubbleProps> = ({
    type,
    direction,
    senderType,
    content,
    isStreaming = false,
    status,
    showStatus = true,
    isLoading = false,
    style,
    textStyle,
    onRetry,
}) => {
    const colors = getBubbleColors(senderType, direction);

    // 系统消息：居中显示，无气泡
    if (type === 'system') {
        return (
            <View style={styles.systemContainer}>
                <Text style={styles.systemText}>{content}</Text>
            </View>
        );
    }

    // 气泡圆角：模仿微信/iMessage 风格
    const bubbleBorderRadius = {
        borderTopLeftRadius: direction === 'left' ? 4 : 16,
        borderTopRightRadius: direction === 'right' ? 4 : 16,
        borderBottomLeftRadius: 16,
        borderBottomRightRadius: 16,
    };

    // 状态图标配置
    const getStatusIcon = () => {
        if (status === 'failed') {
            return { icon: 'alert-circle', color: '#FF3B30', isFailed: true };
        }
        if (direction === 'right') {
            return { icon: 'radio-button-off-outline', color: '#999999', bgColor: '', isFailed: false };
        }
        return null;
    };

    const statusIcon = getStatusIcon();

    // 渲染内容
    const renderContent = () => {
        if (isLoading) {
            return (
                <View style={styles.loadingContainer}>
                    <DbChatLoading dotSize={5} dotColor={colors.text} />
                </View>
            );
        }

        // 如果内容包含 Markdown 标记，使用 Markdown 渲染
        if (containsMarkdown(content)) {
            return (
                <SafeMarkdown
                    content={content}
                    textColor={colors.text}
                    direction={direction}
                    isStreaming={isStreaming}
                />
            );
        }

        // 普通文本
        return (
            <Text style={[styles.bubbleText, { color: colors.text }, textStyle]}>
                {content}
                {isStreaming && <StreamingCursor />}
            </Text>
        );
    };

    return (
        <View style={[styles.bubbleWrapper, direction === 'right' && styles.bubbleWrapperRight]}>
            <View
                style={[
                    styles.bubble,
                    { backgroundColor: colors.bg },
                    bubbleBorderRadius,
                    style,
                ]}
            >
                {renderContent()}
            </View>

            {/* 状态图标（失败/已读/未读），受 showStatus 控制 */}
            {showStatus && statusIcon && (
                <TouchableOpacity
                    style={[
                        styles.statusButton,
                        statusIcon.isFailed ? styles.statusButtonFailed : styles.statusButtonRead,
                        direction === 'right' ? styles.statusButtonLeft : styles.statusButtonRight,
                    ]}
                    onPress={status === 'failed' ? onRetry : undefined}
                    activeOpacity={status === 'failed' ? 0.7 : 1}
                    disabled={status !== 'failed'}
                >
                    <DbIcon
                        name={statusIcon.icon}
                        family="Ionicons"
                        size={statusIcon.isFailed ? 16 : 14}
                        color={statusIcon.color}
                    />
                </TouchableOpacity>
            )}
        </View>
    );
};

DbChatBubble.displayName = 'DbChatBubble';

export default DbChatBubble;
export type { DbChatBubbleProps, MessageType, MessageDirection, MessageStatus, SenderType } from './types';
